import {
  useEffectWithController,
  useLoadableState,
  usePromise
} from '@carvana/merch-react-hooks';
import { useContext, useEffect, useRef, useState } from 'react';
import SortOption from '~/constants/common/SortOption';
import { blobCdnUrl, vexCdnUrl } from '~/constants/environment';
import { InventoryTypeId } from '~/constants/inventoryType';
import DeviceContext, { useIsMobile } from '~/context/common/DeviceContext';
import UserContext from '~/context/common/UserContext';
import { RecommendedVehicle } from '~/contracts/vdp';
import AppliedFilters from '~/services/common/contracts/AppliedFilters';
import fetchInventory from '~/services/common/fetchInventory';
import { BrowserCookies } from '~/utilities';
import generateErrorLogger from '~/utilities/errorHandlers';

function useGetVehicles(
  filters: Partial<AppliedFilters>,
  fetchTrigger: boolean,
  errorLocation: string
) {
  const { browserName } = useContext(DeviceContext);
  const isMobile = useIsMobile();
  const {
    favorites: { data: favorites = [] },
    location: {
      data: { zip = '' }
    }
  } = useContext(UserContext);
  const {
    data: inventory,
    error,
    isLoaded,
    setData,
    setError,
    setLoading
  } = useLoadableState<RecommendedVehicle[]>([]);
  const mounted = usePromise();
  const [shouldFetch, setShouldFetch] = useState(fetchTrigger);
  const favoritesRef = useRef<number[]>([]);
  const isMobileRef = useRef(isMobile);

  useEffect(() => {
    if (fetchTrigger) {
      setShouldFetch(fetchTrigger);
    }
  }, [fetchTrigger, setLoading]);

  useEffect(() => {
    const favoritesIds = favorites.map(favorite => favorite.vehicleId);
    setData(prevVehicles =>
      prevVehicles.map(vehicle => ({
        ...vehicle,
        isFavorited: favoritesIds.includes(vehicle.vehicleId)
      }))
    );
    favoritesRef.current = favoritesIds;
  }, [favorites, setData]);

  useEffect(() => {
    favoritesRef.current = favorites.map(favorite => favorite.vehicleId);
  }, [favorites]);

  useEffect(() => {
    isMobileRef.current = isMobile;
  }, [isMobile]);

  useEffectWithController(
    controller => {
      if (!zip || !shouldFetch) return;

      async function fetchVehicles() {
        setLoading();
        const { data } = await fetchInventory(
          {
            analyticsData: {
              browser: browserName ?? '',
              clientId: 'home_ui',
              deviceName: '',
              isMobileDevice: isMobileRef.current,
              referrer: document.referrer
            },
            browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
            filters: {
              ...filters
            },
            pagination: { page: 1, pageSize: 12 },
            requestedFeatures: [
              'Personalization',
              'LocationBasedPrefiltering',
              'EarliestAcquisitionBoosting'
            ],
            sortBy: SortOption.MostPopular,
            zip5: zip
          },
          controller
        );

        if (data.inventory.vehicles.length === 0) return [];

        return data.inventory.vehicles;
        // TODO current iteration of home redesign doesnt use delivery info. When we bring in the new tile we will want to go back to making this call.
        // try {
        //   const deliveryData = await getBulkAvailabilityInfo(
        //     data.inventory.vehicles,
        //     zip
        //   );
        //
        //   return data.inventory.vehicles.map(vehicle => ({
        //     ...vehicle,
        //     ...deliveryData.find(
        //       vehicleData => vehicleData.vehicleId === vehicle.vehicleId
        //     )
        //   }));
        // } catch (err) {
        //   logError(err, { location: 'Get Soonest Availability' });
        //
        //   return data.inventory.vehicles;
        // }
      }

      mounted(fetchVehicles())
        .then(vehicles => {
          if (vehicles.length === 0) throw new Error('No vehicles found');
          setData(
            vehicles.map<RecommendedVehicle>(vehicle => ({
              ...vehicle,
              isFavorited: favoritesRef.current.includes(vehicle.vehicleId),
              imageUrl:
                vehicle.imageUrl == null ||
                vehicle.vehicleInventoryType ===
                  InventoryTypeId.DealerMarketplace
                  ? `${vexCdnUrl}/${vehicle.stockNumber}/hero.jpg`
                  : `${blobCdnUrl}${vehicle.imageUrl}`
            }))
          );
        })
        .catch(generateErrorLogger(errorLocation, {}, { callback: setError }));
    },
    [
      browserName,
      errorLocation,
      filters,
      mounted,
      setData,
      setError,
      setLoading,
      shouldFetch,
      zip
    ]
  );

  return {
    error,
    isLoaded,
    vehicles: inventory
  };
}

export default useGetVehicles;
